import {
  buildNaturalCapturePipelineSnapshot,
  detectNaturalCaptureInputType,
  traceNaturalCapturePipeline,
} from '../components/Dashboard/ImportLogs/importLogsPipeline';
import {
  resolveNaturalCapturePasteRouting,
} from '../features/careSourceProcessing/parsing';
import {
  extractBackendQuickLogEntries,
  normalizeBackendQuickLogEntries,
  resolveQuickLogLongNoteRows,
} from './quickLogReviewHelpers';
import {
  buildQuickLogCaptureDebugPayload,
  buildQuickLogCaptureErrorPayload,
  buildQuickLogFallbackStructuredEntry,
} from './quickLogSmartCapturePayloads';
import {
  isQuickLogMetaCommentary,
  stripQuickLogMetaCommentary,
} from './quickLogMetaCommentary';

export const QUICK_LOG_SMART_CAPTURE_ACTIONS = {
  EMPTY: 'empty',
  DIRECT_SAVE: 'direct_save',
  AUTO_SAVE_TIMELINE: 'auto_save_timeline',
  SOURCE_PROCESSED: 'source_processed',
  REVIEW: 'review',
};

const getFlowDeps = (deps = {}) => ({
  buildNaturalCapturePipelineSnapshot,
  detectNaturalCaptureInputType,
  traceNaturalCapturePipeline,
  resolveQuickLogLongNoteRows,
  buildQuickLogFallbackStructuredEntry,
  extractBackendQuickLogEntries,
  normalizeBackendQuickLogEntries,
  resolveNaturalCapturePasteRouting,
  buildQuickLogCaptureDebugPayload,
  buildQuickLogCaptureErrorPayload,
  isQuickLogMetaCommentary,
  stripQuickLogMetaCommentary,
  ...deps,
});

const buildReviewPayload = ({
  child,
  text,
  selectedDate,
  selectedTime,
  importantMoment,
  structuredEntries,
  routingPreview,
  usedBackendExtraction,
  structureDecision,
  normalizedText,
  quickLogDebugPayload,
}) => ({
  child,
  text,
  defaultDate: selectedDate,
  selectedTime,
  importantMoment,
  structuredEntries,
  routingPreview,
  usedBackendExtraction,
  structureDecision,
  normalizedText,
  quickLogDebugPayload,
});

export const runQuickLogSmartCaptureFlow = async ({
  text = '',
  normalizedText = '',
  child = null,
  selectedDate = null,
  selectedTime = '',
  importantMoment = false,
  routingDecision = {},
  routingPreview = {},
  transcriptNormalization = {},
  structureDecision = {},
  quickNoteCategoryDecision = null,
  detectedQuickNoteEntities = [],
  hasReviewHandler = false,
  extractQuickLogEntries = null,
  deps = {},
} = {}) => {
  const rawText = String(text || '').trim();
  const normalizedReviewText = String(normalizedText || '').trim();
  const flowDeps = getFlowDeps(deps);

  if (!rawText) {
    return {
      action: QUICK_LOG_SMART_CAPTURE_ACTIONS.EMPTY,
      debugPayload: null,
      reviewPayload: null,
      error: null,
    };
  }

  const shouldUseBackendExtraction = Boolean(routingDecision.shouldUseBackendExtraction);

  try {
    flowDeps.traceNaturalCapturePipeline('quick-log-input', flowDeps.buildNaturalCapturePipelineSnapshot({
      stage: 'quick-log-input',
      inputType: routingDecision.inputType || flowDeps.detectNaturalCaptureInputType({
        source: 'quick_log',
        route: shouldUseBackendExtraction ? 'claude' : 'rule_only',
        isLongNote: routingDecision.mode === 'long_note_review',
        hasStructuredEvents: routingDecision.mode === 'small_structured_review',
        hasStrongSimpleSignal: routingDecision.mode === 'simple_local',
      }),
      text: rawText,
    }));

    const longNoteCapture = routingDecision.mode === 'long_note_review'
      ? flowDeps.resolveQuickLogLongNoteRows({
          text: rawText,
          child,
          defaultDate: selectedDate,
        })
      : null;

    let structuredEntries = routingDecision.mode === 'small_structured_review'
      ? detectedQuickNoteEntities
      : longNoteCapture?.rows || [];

    if (!shouldUseBackendExtraction && (!Array.isArray(structuredEntries) || structuredEntries.length === 0)) {
      structuredEntries = [flowDeps.buildQuickLogFallbackStructuredEntry({
        text: rawText,
        routingDecision,
        transcriptNormalization,
        structureDecision,
      })];
    }

    let backendResponseData = null;
    let backendEntries = [];
    if (shouldUseBackendExtraction) {
      if (typeof extractQuickLogEntries !== 'function') {
        throw new Error('extractQuickLogEntries dependency is required for backend smart capture');
      }

      const backendExtractionText = flowDeps.stripQuickLogMetaCommentary(rawText);
      const result = await extractQuickLogEntries({
        text: backendExtractionText || rawText,
        childId: child?.id || null,
        selectedDate: selectedDate instanceof Date ? selectedDate.toISOString() : selectedDate || null,
        selectedTime,
        importantMoment,
      });

      backendResponseData = result?.data || {};
      backendEntries = flowDeps.extractBackendQuickLogEntries(backendResponseData)
        .filter((entry) => !flowDeps.isQuickLogMetaCommentary(
          entry?.note || entry?.text || entry?.title || ''
        ));
      if (backendResponseData?.ok && backendEntries.length > 0) {
        structuredEntries = flowDeps.normalizeBackendQuickLogEntries(backendEntries, rawText);
      }
    }

    const pasteRoutingDecision = flowDeps.resolveNaturalCapturePasteRouting({
      text: rawText,
      structuredEntries,
      structuredEntryCount: Array.isArray(structuredEntries) ? structuredEntries.length : 0,
      usedBackendExtraction: shouldUseBackendExtraction,
    });
    const shouldPreserveLongNoteRows = routingDecision.mode === 'long_note_review'
      && Array.isArray(longNoteCapture?.rows)
      && longNoteCapture.rows.length > 0;
    const shouldRerouteToImportedNarrativeReview = Boolean(
      !shouldPreserveLongNoteRows
      && pasteRoutingDecision?.shouldUseImportedNarrativeReview
    );

    if (shouldRerouteToImportedNarrativeReview) {
      structuredEntries = [];
    }

    const debugPayload = flowDeps.buildQuickLogCaptureDebugPayload({
      routingDecision,
      transcriptNormalization,
      structureDecision,
      longNoteCapture,
      shouldUseBackendExtraction,
      backendResponseData,
      backendEntries,
      structuredEntries,
      shouldRerouteToImportedNarrativeReview,
      routingPreview,
      text: rawText,
      quickNoteCategoryDecision,
    });

    if (!hasReviewHandler) {
      return {
        action: QUICK_LOG_SMART_CAPTURE_ACTIONS.DIRECT_SAVE,
        debugPayload,
        reviewPayload: null,
        error: null,
      };
    }

    return {
      action: QUICK_LOG_SMART_CAPTURE_ACTIONS.REVIEW,
      debugPayload,
      reviewPayload: buildReviewPayload({
        child,
        text: rawText,
        selectedDate,
        selectedTime,
        importantMoment,
        structuredEntries,
        routingPreview,
        usedBackendExtraction: shouldUseBackendExtraction,
        structureDecision,
        normalizedText: normalizedReviewText,
        quickLogDebugPayload: debugPayload,
      }),
      error: null,
    };
  } catch (error) {
    const fallbackLongNoteCapture = routingDecision.mode === 'long_note_review'
      ? flowDeps.resolveQuickLogLongNoteRows({
          text: rawText,
          child,
          defaultDate: selectedDate,
        })
      : null;
    const errorPayload = flowDeps.buildQuickLogCaptureErrorPayload({
      routingDecision,
      fallbackLongNoteCapture,
      routingPreview,
      text: rawText,
      transcriptNormalization,
      structureDecision,
      error,
    });

    if (!hasReviewHandler) {
      return {
        action: QUICK_LOG_SMART_CAPTURE_ACTIONS.DIRECT_SAVE,
        debugPayload: errorPayload,
        reviewPayload: null,
        error,
      };
    }

    return {
      action: QUICK_LOG_SMART_CAPTURE_ACTIONS.REVIEW,
      debugPayload: errorPayload,
      reviewPayload: buildReviewPayload({
        child,
        text: rawText,
        selectedDate,
        selectedTime,
        importantMoment,
        structuredEntries: routingDecision.mode === 'small_structured_review'
          ? detectedQuickNoteEntities
          : (fallbackLongNoteCapture?.rows || []),
        routingPreview,
        usedBackendExtraction: shouldUseBackendExtraction,
        structureDecision,
        normalizedText: normalizedReviewText,
        quickLogDebugPayload: errorPayload,
      }),
      error,
    };
  }
};
